import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS_NAME } from '../../constants';
import { apiFetch } from '../../lib/api';
import type { BusinessConfig } from '@gbty/shared';

const FOOTER_NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/ornaments', label: 'Ornaments' },
  { to: '/occasions', label: 'Occasions' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact' },
  { to: '/order', label: 'Order' },
] as const;

/** Facebook brand SVG icon (24x24) */
function FacebookIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="h-6 w-6"
      aria-hidden="true"
    >
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [facebookUrl, setFacebookUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<BusinessConfig>('/v1/config/business')
      .then((config) => {
        if (!cancelled && config.facebookUrl) {
          setFacebookUrl(config.facebookUrl);
        }
      })
      .catch(() => {
        // Silently ignore — Facebook link is non-critical
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="border-t border-surface-footer-border bg-surface-footer text-surface-footer-text">
      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
          {/* Business info */}
          <div className="text-center md:text-left">
            <p className="font-bold text-white">{BUSINESS_NAME}</p>
            <div className="mt-1 flex items-center justify-center gap-3 md:justify-start">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-sm text-surface-footer-text transition-colors hover:text-white"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
                Send us a Message
              </Link>
              {facebookUrl && (
                <a
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-surface-footer-text transition-colors hover:text-white"
                  aria-label={`Visit ${BUSINESS_NAME} on Facebook`}
                >
                  <FacebookIcon />
                </a>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav
            className="flex flex-wrap justify-center gap-4 md:gap-6"
            aria-label="Footer navigation"
          >
            {FOOTER_NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-surface-footer-text transition-colors hover:text-white"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Copyright */}
        <div className="mt-6 border-t border-surface-footer-border pt-6 text-center text-xs text-surface-footer-text-muted">
          &copy; {currentYear} {BUSINESS_NAME}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
